import { useQueryClient } from '@tanstack/react-query';
import { type LeadListQuery } from '@utazzo/shared';
import { cn } from '@utazzo/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CARRYFORWARD_FLOOR_ISO, getPrevMonthEndIso } from '../carryforward';
import { istIsoDate } from '../lead-date-range';
import * as leadsApi from '../leads.api';
import { useCanSeeLeadApprovals, useLeadApprovalsPendingCount } from '../use-lead-approvals';
import { useQueryStatusOptionsWithBucket } from '../use-lead-options';
import { useLeadsList } from '../use-leads';

/**
 * Daily Leads Tracker strip - the enriched summary that sits at the top of
 * the Lead Inbox "Total Pool" tab.
 *
 * 2026-07-26 redesign. Each tile now shows THREE numbers:
 *   - a big COMBINED figure (the whole pool, no date filter) as the
 *     headline, and
 *   - two small breakdown rows underneath: "This month" (current calendar
 *     month) and "Carry forward" (prior-month OPEN backlog).
 *
 * The previously-separate "Current Month" and "Carry Forward" strips were
 * folded into these breakdown rows, so this one strip replaces all three.
 * The counts come from `useTrackerCounts`, called once per slice
 * (combined / current-month / carry-forward); React Query dedupes the
 * shared master lists, so the request count is unchanged from the old
 * three-strip layout. The COMBINED tile is the clickable, actionable one;
 * the two breakdown numbers are read-outs.
 *
 * Legacy parity with `legacy-flows-mapping.md` → "Daily Leads Tracker"
 * (tile-to-filter mapping, ADR-0032 §22). The combined slice has NO
 * `createdAt` filter (ops asked for the whole pool - carry-forward backlog
 * + current month + everything else in one number). Drill-ins carry ONLY the
 * tile's own filter (stage / bucket / follow-up) + owner: the detail aside
 * shows the combined working pool as its FIXED default (2026-08-04), floored
 * at the carry-forward cutover, so the landed list + chips always equal the
 * tile with no `f.combined` param and no date range in the URL.
 */
type TrackerTone = 'neutral' | 'open' | 'won' | 'lost' | 'warning';

// Tracker tile chrome is kept consistent across all tones: the tint only
// drives the headline number's colour (red = missed, green = today's,
// amber = not qualified, blue = fresh, neutral = revert / total), while
// borders / fills / hover stay identical so the strip doesn't read as
// noise.
const TILE_BG = 'border-zinc-200 bg-white';
const TILE_HOVER = 'hover:border-zinc-300 hover:bg-zinc-50';

const TONE_VALUE: Record<TrackerTone, string> = {
  neutral: 'text-zinc-900',
  open: 'text-blue-700',
  won: 'text-emerald-700',
  lost: 'text-red-700',
  warning: 'text-amber-700',
};

const fmt = (n: number): string => n.toLocaleString('en-IN');

/** Two-number breakdown shown under a tile's headline. Defaults to the
 *  this-month / carry-forward split, but the labels can be overridden (e.g. the
 *  follow-up tiles split by Follow-up / First Contact instead). */
interface TileBreakdown {
  currentMonth: number;
  carryForward: number;
  currentLabel?: string;
  carryLabel?: string;
  /** Optional third row - the no-agent (Unassigned) slice of this status, shown
   *  for the tiles that fold the unassigned pool into their headline (NQ /
   *  Active). When set, headline = currentMonth + carryForward + unassigned. */
  unassigned?: number;
  unassignedLabel?: string;
  loading?: boolean;
}

interface TrackerTileProps {
  label: string;
  sublabel?: string;
  /** Headline (combined / whole-pool) figure. */
  value: number;
  loading?: boolean;
  tone: TrackerTone;
  disabled?: boolean;
  onClick?: () => void;
  /** Optional This-month / Carry-forward split under the headline. */
  breakdown?: TileBreakdown;
}

/**
 * Enriched tracker tile - horizontal layout. Big combined headline + label
 * on the LEFT, and the optional "This month / Carry forward" breakdown on
 * the RIGHT (using the otherwise-empty right-hand space, so the tile stays
 * short instead of stacking everything vertically). A hairline divides the
 * two columns. Renders as a button (clickable drill-in) unless `disabled`
 * or no `onClick` is supplied, in which case it's an inert read-out.
 */
const TrackerTile = ({
  label,
  sublabel,
  value,
  loading,
  tone,
  disabled,
  onClick,
  breakdown,
}: TrackerTileProps): React.JSX.Element => {
  const inert = disabled || !onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={inert}
      className={
        'group flex min-w-37.5 items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors ' +
        TILE_BG +
        ' ' +
        (inert ? 'cursor-default opacity-95' : 'cursor-pointer hover:shadow-md ' + TILE_HOVER)
      }
    >
      {/* Left column - headline + label. */}
      <div className="min-w-0">
        <div
          className={`text-[26px] leading-none font-semibold tracking-tight tabular-nums ${TONE_VALUE[tone]}`}
        >
          {loading ? '…' : fmt(value)}
        </div>
        <div className="mt-1 truncate text-[13px] leading-tight font-medium tracking-wide text-zinc-700">
          {label}
        </div>
        {sublabel && (
          <div className="truncate text-[11px] leading-tight text-zinc-500">{sublabel}</div>
        )}
      </div>
      {/* Right column - breakdown read-outs, filling the empty right space. */}
      {breakdown && (
        <div className="shrink-0 space-y-0.5 self-center border-l border-zinc-100 pl-2.5">
          <div className="flex items-center justify-between gap-1.5">
            <span className="text-[10px] font-medium tracking-wide text-zinc-400">
              {breakdown.currentLabel ?? 'Crnt Mnth'}
            </span>
            <span className="text-[11px] font-semibold text-zinc-700 tabular-nums">
              {breakdown.loading ? '…' : fmt(breakdown.currentMonth)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-1.5">
            <span className="text-[10px] font-medium tracking-wide text-zinc-400">
              {breakdown.carryLabel ?? 'Carry fwd'}
            </span>
            <span className="text-[11px] font-semibold text-zinc-700 tabular-nums">
              {breakdown.loading ? '…' : fmt(breakdown.carryForward)}
            </span>
          </div>
          {breakdown.unassigned !== undefined && (
            <div className="flex items-center justify-between gap-1.5">
              <span className="text-[10px] font-medium tracking-wide text-zinc-400">
                {breakdown.unassignedLabel ?? 'Unassigned'}
              </span>
              <span className="text-[11px] font-semibold text-zinc-700 tabular-nums">
                {breakdown.loading ? '…' : fmt(breakdown.unassigned)}
              </span>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

// --------------------------------------------------------------------------
// TodayTile - the Claude Design v1 "Daily tracker (today only)" tile. Vertical
// layout: dot + label, 28px number, then a hint line or a two-stat split. The
// palette is hard-coded (fixed teal-on-#fafafa board); drill wiring is unchanged
// from the tiles it replaces. See docs/reporting/lead-inbox-redesign-spec.md.
// --------------------------------------------------------------------------
interface TodayTileProps {
  label: string;
  value: number;
  loading?: boolean;
  /** Filled dark card (Total tasks). */
  dark?: boolean;
  /** Status dot colour; omitted on the dark card. */
  dotColor?: string;
  /** Big-number colour. */
  numberColor: string;
  /** Single hint line (when there is no split). */
  hint?: React.ReactNode;
  /** Two inline read-outs under the number (e.g. Follow-up / First contact). */
  split?: { leftLabel: string; left: number; rightLabel: string; right: number };
  disabled?: boolean;
  onClick?: () => void;
}

const TodayTile = ({
  label,
  value,
  loading,
  dark = false,
  dotColor,
  numberColor,
  hint,
  split,
  disabled,
  onClick,
}: TodayTileProps): React.JSX.Element => {
  const inert = disabled || !onClick;
  const chrome = dark
    ? 'border-[#094053] bg-[#094053]'
    : 'border-[#e4e4e7] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]';
  const hover = inert
    ? 'cursor-default'
    : dark
      ? 'cursor-pointer'
      : 'cursor-pointer hover:border-[#a1a1aa] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={inert}
      className={cn(
        'flex w-full flex-col gap-2 rounded-xl border px-[14px] py-[12px] text-left transition-[border-color,box-shadow]',
        chrome,
        hover,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-[6px] text-[12px] font-semibold',
          dark ? 'text-[#d4d4d8]' : 'text-[#52525b]',
        )}
      >
        {dotColor && <span className="h-2 w-2 rounded-full" style={{ background: dotColor }} />}
        {label}
      </div>
      <div
        className="text-[28px] leading-none font-semibold tracking-[-0.02em] tabular-nums"
        style={{ color: numberColor }}
      >
        {loading ? '…' : fmt(value)}
      </div>
      {split ? (
        <div
          className={cn(
            'flex flex-wrap gap-x-3 gap-y-1 text-[11px] whitespace-nowrap',
            dark ? 'text-[#a1a1aa]' : 'text-[#71717a]',
          )}
        >
          <span>
            {split.leftLabel}{' '}
            <b className="font-semibold text-[#094053] tabular-nums">{fmt(split.left)}</b>
          </span>
          <span>
            {split.rightLabel}{' '}
            <b className="font-semibold text-[#094053] tabular-nums">{fmt(split.right)}</b>
          </span>
        </div>
      ) : (
        <div className={cn('text-[11px]', dark ? 'text-[#a1a1aa]' : 'text-[#71717a]')}>{hint}</div>
      )}
    </button>
  );
};

// --------------------------------------------------------------------------
// useTrackerCounts - all per-tile counts for ONE slice of the lead pool.
// --------------------------------------------------------------------------

type TrackerMode = 'all' | 'carryforward' | 'current-month';

interface TrackerCounts {
  freshToday: number;
  notQualified: number;
  revertPending: number;
  largeLeadQuery: number;
  highIntent: number;
  todayFollowUps: number;
  missedFollowUps: number;
  totalLeads: number;
  activeLeads: number;
  longFollowup: number;
  unassigned: number;
  /** No-agent slices folded into the NQ / Active / Large-lead headlines. */
  unassignedNotQualified: number;
  unassignedActive: number;
  unassignedLargeLead: number;
  totalTasks: number;
  won: number;
  lost: number;
  loading: Record<
    | 'freshToday'
    | 'notQualified'
    | 'revertPending'
    | 'largeLeadQuery'
    | 'highIntent'
    | 'todayFollowUps'
    | 'missedFollowUps'
    | 'totalLeads'
    | 'activeLeads'
    | 'longFollowup'
    | 'unassigned'
    | 'totalTasks'
    | 'won'
    | 'lost',
    boolean
  >;
  /** First matching lead id per tile - the drill-in target (combined slice). */
  firstId: { freshToday?: string; notQualified?: string; won?: string; lost?: string };
  notQualifiedStatusId?: string;
  activeStatusId?: string;
  longFollowupStatusId?: string;
  /** The slice's createdAt / owner scope - reused to build drill-in queries. */
  operationalCreatedRange: Partial<LeadListQuery>;
  stagesLoading: boolean;
}

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Compute every Daily-Tracker tile count for a single slice of the pool.
 * `mode` picks the createdAt scope:
 *   - 'all'           : NO date filter (the whole pool - the headline).
 *   - 'current-month' : month-start -> today (pinned; the "This month" row).
 *   - 'carryforward'  : floor -> prev-month-end, OPEN bucket (the "Carry
 *                       forward" row).
 * Called once per slice from `LeadsTrackerStrip`. The master lists
 * (`useQueryStatusOptionsWithBucket`, sub-statuses) dedupe across calls, so
 * three calls cost only the tile counts - the same set the old three-strip
 * layout fired.
 */
const useTrackerCounts = (
  mode: TrackerMode,
  ownerId?: string | null,
  // When the inbox header has a SPECIFIC range picked (anything but the default
  // this_month), these bound the 'all' slice so every headline tile honours the
  // filter. Only consulted for mode 'all'; the current-month / carry-forward
  // slices keep their pinned windows. Both null => whole pool (unbounded), which
  // is also how 'all' (all-time) resolves.
  rangeFrom?: string | null,
  rangeTo?: string | null,
): TrackerCounts => {
  const isCarryforward = mode === 'carryforward';
  const isCurrentMonth = mode === 'current-month';
  // Fresh Today = leads created today. Today is inside the current calendar
  // month, so it belongs to the CURRENT-MONTH slice (its Total Tasks must
  // include it, otherwise the combined Total Tasks - which does include
  // Fresh - overshoots the breakdown by the fresh count). Carry-forward is
  // by definition pre-month, so a lead created today can never be CF - skip
  // it there only. The Fresh Today TILE itself still reads off the combined
  // slice.
  const skipFresh = mode === 'carryforward';

  const todayIso = istIsoDate();
  const yesterdayIso = (() => {
    const now = new Date();
    return istIsoDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  })();
  const prevMonthEndIso = getPrevMonthEndIso();
  const currentMonthStartIso = (() => {
    const [y, m] = todayIso.split('-');
    return `${y}-${m}-01`;
  })();

  const stagesQuery = useQueryStatusOptionsWithBucket();
  const notQualifiedStatusId = useMemo(
    () => stagesQuery.data.find((s) => s.code === 'not_qualified')?.id,
    [stagesQuery.data],
  );
  const activeStatusId = useMemo(
    () => stagesQuery.data.find((s) => s.code === 'active')?.id,
    [stagesQuery.data],
  );
  const longFollowupStatusId = useMemo(
    () => stagesQuery.data.find((s) => s.code === 'long_followup')?.id,
    [stagesQuery.data],
  );

  // The whole pool counts assigned + unassigned + long-followup leads (website
  // stays out - a separate flow). `includeUnassignedPool` folds the exit-released
  // queue back in for unscoped (admin) views; the long_followup status is no
  // longer fenced out, so it lands in Total. Per-owner (agent) views pin
  // `assignedAgentId`, so unassigned never leaks into them.
  const ownerFragment = {
    excludeWebsite: true as const,
    includeUnassignedPool: true as const,
    ...(ownerId ? { assignedAgentId: ownerId } : {}),
  };

  // Slice scope. Combined = no date filter (whole pool). Current month =
  // pinned month window. Carry forward = floored prior-month OPEN slice.
  const operationalCreatedRange = useMemo((): Partial<LeadListQuery> => {
    if (isCarryforward) {
      return {
        createdAtFrom: CARRYFORWARD_FLOOR_ISO,
        createdAtTo: prevMonthEndIso,
        currentBucket: 'OPEN',
        ...ownerFragment,
      };
    }
    if (isCurrentMonth) {
      return {
        createdAtFrom: currentMonthStartIso,
        createdAtTo: todayIso,
        ...ownerFragment,
      };
    }
    // 'all' slice. When the header has a specific range picked, scope the whole
    // pool to it so every headline tile filters; otherwise stay unbounded.
    return {
      ...ownerFragment,
      ...(rangeFrom ? { createdAtFrom: rangeFrom } : {}),
      ...(rangeTo ? { createdAtTo: rangeTo } : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCarryforward,
    isCurrentMonth,
    prevMonthEndIso,
    currentMonthStartIso,
    todayIso,
    ownerId,
    longFollowupStatusId,
    rangeFrom,
    rangeTo,
  ]);

  // -- Today's Fresh Leads (combined slice only) --
  const freshTodayQuery = useLeadsList(
    !skipFresh
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          createdAtFrom: todayIso,
          createdAtTo: todayIso,
          ...ownerFragment,
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentBucket: 'WON', currentStatusId: ZERO_UUID },
  );

  // -- Not Qualified -- carry-forward has its own floored OPEN slice.
  const notQualifiedQuery = useLeadsList(
    !notQualifiedStatusId
      ? { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: ZERO_UUID }
      : isCarryforward
        ? {
            page: 1,
            pageSize: 1,
            sortDir: 'desc',
            currentStatusId: notQualifiedStatusId,
            createdAtFrom: CARRYFORWARD_FLOOR_ISO,
            createdAtTo: prevMonthEndIso,
            currentBucket: 'OPEN',
            ...ownerFragment,
            // Assigned-only: the no-agent NQ leads are counted by the separate
            // Unassigned slice, so Current + Carry + Unassigned stays disjoint.
            assignedAgentIsNotNull: true,
          }
        : {
            page: 1,
            pageSize: 1,
            sortDir: 'desc',
            currentStatusId: notQualifiedStatusId,
            ...operationalCreatedRange,
            assignedAgentIsNotNull: true,
          },
  );

  // -- Active Leads -- the parent `active` stage, OPEN bucket.
  //
  // Counted off the PARENT status, NOT as proposal + hot + warm. The
  // sub-status sum over-counts on drifted rows: a lead that left the Active
  // stage (or closed) while keeping a leftover sub-status tag - common on
  // migrated rows - still lands in the sub-status totals. That is exactly
  // why the tile read one higher than the Active list it opens.
  // `currentStatusId=active + currentBucket=OPEN` is the same predicate the
  // drill-in below, the aside's Active chip, and the pipeline grid use, so
  // every surface now answers with the same number.
  const activeLeadsQuery = useLeadsList(
    activeStatusId
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          currentStatusId: activeStatusId,
          currentBucket: 'OPEN',
          ...operationalCreatedRange,
          // Assigned-only; unassigned Active is the separate Unassigned slice.
          assignedAgentIsNotNull: true,
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: ZERO_UUID },
  );

  const revertPendingQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    revertPending: true,
    currentBucket: 'OPEN',
    ...operationalCreatedRange,
    ...(notQualifiedStatusId ? { excludeStatusId: notQualifiedStatusId } : {}),
  });
  const largeLeadQueryCountQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    largeLeadQuery: true,
    currentBucket: 'OPEN',
    ...operationalCreatedRange,
    // Assigned-only; unassigned large leads counted by unassignedLargeLeadQuery.
    assignedAgentIsNotNull: true,
  });
  // High Intent = OPEN leads whose auto-scored Lead Intent (priority) is HIGH.
  const highIntentCountQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    priority: 'HIGH',
    currentBucket: 'OPEN',
    ...operationalCreatedRange,
  });
  // 2026-08-01. Follow-up tiles count EVERY lead with a follow-up due, including
  // Not Qualified. The old NQ exclusion assumed "NQ = never contacted, so no
  // follow-up", but the data has many NQ leads carrying real follow-up dates -
  // hiding them undercounted the operator's actual due work.
  const followUpTodayQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    currentBucket: 'OPEN',
    nextFollowUpFrom: todayIso,
    nextFollowUpTo: todayIso,
    ...operationalCreatedRange,
  });
  const followUpMissedQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    currentBucket: 'OPEN',
    nextFollowUpTo: yesterdayIso,
    ...operationalCreatedRange,
  });
  // Total Leads = the OPEN working pool + this period's closures (Won/Lost),
  // which are disjoint sets (a lead is open XOR closed). This query is the OPEN
  // half; the strip adds Won + Lost on top. currentBucket:'OPEN' overrides the
  // current-month slice's no-bucket default (carry-forward already pins OPEN),
  // so a this-month-created lead that already closed isn't double-counted here.
  const totalScopeQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    ...operationalCreatedRange,
    currentBucket: 'OPEN',
    // Assigned-only OPEN pool; the full no-agent pool is added on top as the
    // Total Leads tile's Unassigned breakdown row (combined.unassigned).
    assignedAgentIsNotNull: true,
  });

  // Total Tasks = DISTINCT union of the five task categories (Fresh + NQ +
  // Revert + Today FU + Missed FU). The server ORs them (`dailyTaskPool`) so a
  // lead in several - e.g. Not Qualified AND overdue for follow-up - is counted
  // ONCE, not summed. `operationalCreatedRange` scopes it to the slice + owner.
  const dailyTaskPoolQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    dailyTaskPool: true,
    ...operationalCreatedRange,
  });

  // Won / Lost. Carry forward asks the created-cohort question (dateBasis
  // 'created', floored prior-month window); the other slices use the slice
  // range directly (server reroutes createdAt* -> closedAt* for terminal
  // buckets, ADR-0058, so "won/lost IN this slice").
  const carryforwardClosedRange = {
    dateBasis: 'created' as const,
    createdAtFrom: CARRYFORWARD_FLOOR_ISO,
    createdAtTo: prevMonthEndIso,
    ...ownerFragment,
  };
  // Won / Lost are assigned-only (Current + Carry). Per product: unassigned
  // closed leads are out of scope ("not worth"), and the Unassigned pool is
  // OPEN-bucket only anyway, so this just keeps the two disjoint and summable.
  const wonQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    currentBucket: 'WON',
    assignedAgentIsNotNull: true,
    ...(isCarryforward ? carryforwardClosedRange : operationalCreatedRange),
  });
  const lostQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    currentBucket: 'LOST',
    assignedAgentIsNotNull: true,
    ...(isCarryforward ? carryforwardClosedRange : operationalCreatedRange),
  });
  // -- Long follow-up -- leads parked on the `long_followup` status (OPEN).
  const longFollowupQuery = useLeadsList(
    longFollowupStatusId
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          currentStatusId: longFollowupStatusId,
          ...operationalCreatedRange,
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: ZERO_UUID },
  );

  // -- Unassigned -- every lead with no agent (website excluded). Owner filter
  // does not apply (unassigned leads have no owner); it's an org-wide pool.
  const unassignedQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    excludeWebsite: true,
    assignedAgentIsNull: true,
    ...(isCarryforward
      ? {
          createdAtFrom: CARRYFORWARD_FLOOR_ISO,
          createdAtTo: prevMonthEndIso,
          currentBucket: 'OPEN' as const,
        }
      : isCurrentMonth
        ? { createdAtFrom: currentMonthStartIso, createdAtTo: todayIso }
        : // 'all' slice: Unassigned is a fixed GLOBAL count - every no-agent
          // lead, never scoped by the header range. So no date window here even
          // when a range is active.
          {}),
  });

  // -- Unassigned by status -- the no-agent slice folded into the NQ / Active /
  // Large-lead tile headlines (Current + Carry + Unassigned). These are FIXED
  // global counts (never month-scoped), so all three slice calls issue the same
  // query and React Query dedupes them. assignedAgentIsNull already pins the
  // backend to the OPEN bucket, so closed unassigned leads never leak in.
  const unassignedNqQuery = useLeadsList(
    notQualifiedStatusId
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          excludeWebsite: true,
          assignedAgentIsNull: true,
          currentStatusId: notQualifiedStatusId,
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: ZERO_UUID },
  );
  const unassignedActiveQuery = useLeadsList(
    activeStatusId
      ? {
          page: 1,
          pageSize: 1,
          sortDir: 'desc',
          excludeWebsite: true,
          assignedAgentIsNull: true,
          currentStatusId: activeStatusId,
        }
      : { page: 1, pageSize: 1, sortDir: 'desc', currentStatusId: ZERO_UUID },
  );
  const unassignedLargeLeadQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    excludeWebsite: true,
    assignedAgentIsNull: true,
    largeLeadQuery: true,
  });

  const freshToday = freshTodayQuery.data?.pagination.totalItems ?? 0;
  const notQualified = notQualifiedQuery.data?.pagination.totalItems ?? 0;
  const longFollowup = longFollowupQuery.data?.pagination.totalItems ?? 0;
  const unassigned = unassignedQuery.data?.pagination.totalItems ?? 0;
  const revertPending = revertPendingQuery.data?.pagination.totalItems ?? 0;
  const largeLeadQuery = largeLeadQueryCountQuery.data?.pagination.totalItems ?? 0;
  const highIntent = highIntentCountQuery.data?.pagination.totalItems ?? 0;
  const todayFollowUps = followUpTodayQuery.data?.pagination.totalItems ?? 0;
  const missedFollowUps = followUpMissedQuery.data?.pagination.totalItems ?? 0;
  const totalLeads = totalScopeQuery.data?.pagination.totalItems ?? 0;
  const won = wonQuery.data?.pagination.totalItems ?? 0;
  const lost = lostQuery.data?.pagination.totalItems ?? 0;
  const activeLeads = activeLeadsQuery.data?.pagination.totalItems ?? 0;
  const unassignedNotQualified = unassignedNqQuery.data?.pagination.totalItems ?? 0;
  const unassignedActive = unassignedActiveQuery.data?.pagination.totalItems ?? 0;
  const unassignedLargeLead = unassignedLargeLeadQuery.data?.pagination.totalItems ?? 0;
  // Total Tasks = DISTINCT count of leads needing action across the five task
  // categories (server-side union via `dailyTaskPool`) - NOT the sum, which
  // double-counts leads that fall in several tiles (e.g. an NQ lead that also
  // has a missed follow-up). Each such lead is one thing to do, counted once.
  const totalTasks = dailyTaskPoolQuery.data?.pagination.totalItems ?? 0;

  const activeLeadsLoading = stagesQuery.isLoading || activeLeadsQuery.isLoading;

  return {
    freshToday,
    notQualified,
    revertPending,
    largeLeadQuery,
    highIntent,
    todayFollowUps,
    missedFollowUps,
    totalLeads,
    activeLeads,
    longFollowup,
    unassigned,
    unassignedNotQualified,
    unassignedActive,
    unassignedLargeLead,
    totalTasks,
    won,
    lost,
    loading: {
      freshToday: freshTodayQuery.isLoading,
      notQualified:
        notQualifiedQuery.isLoading || unassignedNqQuery.isLoading || stagesQuery.isLoading,
      revertPending: revertPendingQuery.isLoading,
      largeLeadQuery: largeLeadQueryCountQuery.isLoading || unassignedLargeLeadQuery.isLoading,
      highIntent: highIntentCountQuery.isLoading,
      todayFollowUps: followUpTodayQuery.isLoading,
      missedFollowUps: followUpMissedQuery.isLoading,
      totalLeads: totalScopeQuery.isLoading,
      activeLeads: activeLeadsLoading || unassignedActiveQuery.isLoading,
      longFollowup: longFollowupQuery.isLoading || stagesQuery.isLoading,
      unassigned: unassignedQuery.isLoading,
      totalTasks: dailyTaskPoolQuery.isLoading,
      won: wonQuery.isLoading,
      lost: lostQuery.isLoading,
    },
    firstId: {
      freshToday: freshTodayQuery.data?.data[0]?.id,
      notQualified: notQualifiedQuery.data?.data[0]?.id,
      won: wonQuery.data?.data[0]?.id,
      lost: lostQuery.data?.data[0]?.id,
    },
    notQualifiedStatusId,
    activeStatusId,
    longFollowupStatusId,
    operationalCreatedRange,
    stagesLoading: stagesQuery.isLoading,
  };
};

interface LeadsTrackerStripProps {
  /**
   * 2026-07-08. Owner scope from the inbox's role-hierarchy filter. When
   * set, every tile (and its breakdown) intersects with
   * `assignedAgentId=<ownerId>`.
   */
  ownerId?: string | null;
  /**
   * Display name of the picked owner - only used to label the Pending
   * Approvals tile ("Raised by <name>").
   */
  ownerName?: string | null;
  /**
   * Inbox header date-range preset key. When it is anything other than the
   * default `this_month`, the picked window scopes every tile (headline +
   * drill-in) and the This-month / Carry-forward breakdown collapses into a
   * single range-scoped number. `all` scopes to the whole pool.
   */
  presetKey?: string | null;
  /** Resolved lower bound of the header range (YYYY-MM-DD), or null. */
  createdAtFrom?: string | null;
  /** Resolved upper bound of the header range (YYYY-MM-DD), or null. */
  createdAtTo?: string | null;
  /**
   * 2026-10-01. When true, render ONLY the five live "Today" tiles (row 1) and
   * drop the pipeline/outcome row (Total Leads / NQ / Active / Won / Lost) and
   * the queues row. The pool metrics now live in the month-filtered report's
   * Total / Current / Carry sections (single source of truth), so the inbox no
   * longer duplicates them here - which is what made the inbox 1,339 disagree
   * with the report's 1,340.
   */
  todayOnly?: boolean;
}

/**
 * The enriched Daily Tracker strip. Big combined headline per tile with a
 * This-month / Carry-forward breakdown underneath. Combined tiles are
 * clickable (drill into the detail aside via `f.combined=yes`, so the list
 * total matches the tile); the breakdown numbers are read-outs.
 */
export const LeadsTrackerStrip = ({
  ownerId,
  ownerName,
  presetKey,
  createdAtFrom,
  createdAtTo,
  todayOnly = false,
}: LeadsTrackerStripProps = {}): React.JSX.Element => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // A specific header range (anything but the default this_month) drives every
  // tile: the combined slice is scoped to [from,to] and the This-month /
  // Carry-forward breakdown collapses into that single window. `all` keeps the
  // whole pool (null bounds). See the wonWindow pattern in LeadInboxPage.
  const rangeActive = !!presetKey && presetKey !== 'this_month';
  const rangeFrom = rangeActive && presetKey !== 'all' ? (createdAtFrom ?? null) : null;
  const rangeTo = rangeActive && presetKey !== 'all' ? (createdAtTo ?? null) : null;
  // Nav suffix (`&daterange=...&from=...&to=...`) forwarded on range-scoped
  // drill-ins so the landed list matches the tile. Empty when not range-active.
  const rangeNavParams = rangeActive
    ? [
        'daterange=' + presetKey,
        rangeFrom ? 'from=' + rangeFrom : '',
        rangeTo ? 'to=' + rangeTo : '',
      ]
        .filter(Boolean)
        .join('&')
    : '';

  // One call per slice. `combined` drives every headline + the drill-in;
  // `currentMonth` / `carryForward` fill the two breakdown rows (unused when a
  // range is active - the headline then reads the range-scoped combined slice).
  const combined = useTrackerCounts('all', ownerId, rangeFrom, rangeTo);
  const currentMonth = useTrackerCounts('current-month', ownerId);
  const carryForward = useTrackerCounts('carryforward', ownerId);

  const todayIso = istIsoDate();
  const yesterdayIso = (() => {
    const now = new Date();
    return istIsoDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  })();

  const { notQualifiedStatusId, operationalCreatedRange } = combined;

  // 2026-08-04. Combined is the aside's fixed default now (no `f.combined`
  // param), so the drill-in only carries the owner scope. The aside always
  // shows the whole working pool, so the landed list already matches the tile.
  const navRangeSuffix = ((): string => {
    const parts: string[] = rangeActive ? [rangeNavParams] : [];
    if (ownerId) parts.push(`ownerId=${ownerId}`);
    return parts.filter(Boolean).join('&');
  })();

  const firstLeadId = (id: string | undefined): string | undefined => id;

  const openTile = (filterSuffix: string, leadId: string | undefined): void => {
    if (!leadId) return;
    const tileParams = [filterSuffix, navRangeSuffix].filter(Boolean).join('&');
    navigate(`/leads/${leadId}${tileParams ? `?${tileParams}` : ''}`);
  };

  // Won / Lost drill-in. Those tiles show ONLY the current-month count
  // (closedAt this month), so their drill-in must NOT carry `f.combined`
  // (that flag drops the date window and would list every won/lost lead).
  // With just the owner scope the aside falls back to its default
  // `this_month` range, and the server reroutes createdAt->closedAt for
  // terminal buckets (ADR-0058), so the list shows "won/lost this month"
  // and its Total matches the tile.
  const openTileMonth = (filterSuffix: string, leadId: string | undefined): void => {
    if (!leadId) return;
    const ownerSuffix = ownerId ? `ownerId=${ownerId}` : '';
    // Range active: forward the picked window (server reroutes createdAt->
    // closedAt for terminal buckets, ADR-0058, so "won/lost IN the range").
    // Otherwise no range suffix -> the aside falls back to `this_month`.
    const tileParams = [filterSuffix, rangeActive ? rangeNavParams : '', ownerSuffix]
      .filter(Boolean)
      .join('&');
    navigate(`/leads/${leadId}${tileParams ? `?${tileParams}` : ''}`);
  };

  /// Fetch the first matching lead id on click, then open the two-pane
  /// detail screen with the tile's filter params so the aside list +
  /// bracket counts stay in the same slice as the tile count.
  const openTileViaLazyFetch = async (
    tileKey: string,
    query: LeadListQuery,
    filterSuffix: string,
  ): Promise<void> => {
    try {
      const result = await qc.fetchQuery({
        queryKey: ['leads', 'tile-first-id', tileKey, query],
        queryFn: () => leadsApi.listLeads(query),
        staleTime: 5_000,
      });
      const id = result.data[0]?.id;
      const tileParams = [filterSuffix, navRangeSuffix].filter(Boolean).join('&');
      if (id) {
        navigate(`/leads/${id}${tileParams ? `?${tileParams}` : ''}`);
      } else {
        navigate(`/leads?tab=pipeline${tileParams ? `&${tileParams}` : ''}`);
      }
    } catch {
      navigate(`/leads?tab=pipeline${filterSuffix ? `&${filterSuffix}` : ''}`);
    }
  };

  // Total Leads is an aggregate (This month + Carry forward), not a single
  // tracker slice - so it can't use the `f.combined=yes` drill-in (that flag
  // drops every date bound and lists the whole all-time pool). It routes
  // into the SAME `includeActiveCarryover` pool the Pipeline grid uses
  // (this-month window + still-OPEN carry-forward backlog, floored at the
  // cutover anchor) via the `f.activeCarryover=yes` flag, so the landed
  // list's Total equals the tile's This month + Carry forward number. Owner
  // scope rides along; `f.combined` is intentionally NOT appended here.
  const currentMonthKey = todayIso.slice(0, 7); // YYYY-MM (IST), matches the aside default.
  const openWorkingPool = (query: LeadListQuery, filterSuffix: string): void => {
    void (async () => {
      const ownerSuffix = ownerId ? `ownerId=${ownerId}` : '';
      // Range active: the tile shows the range-scoped combined count, so the
      // drill-in must land the SAME window. Strip the default this-month +
      // carry-forward tokens from the suffix (keep stage/bucket), swap in the
      // picked window, and override the first-id fetch query to match.
      const effFilterSuffix = rangeActive
        ? filterSuffix
            .split('&')
            .filter((t) => t && !t.startsWith('daterange=') && t !== 'f.activeCarryover=yes')
            .join('&')
        : filterSuffix;
      const effQuery: LeadListQuery = rangeActive
        ? {
            ...query,
            includeActiveCarryover: undefined,
            activeCarryoverFrom: undefined,
            createdAtFrom: rangeFrom ?? undefined,
            createdAtTo: rangeTo ?? undefined,
          }
        : query;
      const tileParams = [effFilterSuffix, rangeActive ? rangeNavParams : '', ownerSuffix]
        .filter(Boolean)
        .join('&');
      try {
        const result = await qc.fetchQuery({
          queryKey: ['leads', 'tile-first-id', effFilterSuffix, effQuery],
          queryFn: () => leadsApi.listLeads(effQuery),
          staleTime: 5_000,
        });
        const id = result.data[0]?.id;
        if (id) {
          navigate(`/leads/${id}${tileParams ? `?${tileParams}` : ''}`);
          return;
        }
      } catch {
        // fall through to the tab-level fallback below
      }
      navigate(`/leads?tab=pipeline${tileParams ? `&${tileParams}` : ''}`);
    })();
  };

  // Pending Approvals - queue depth, no date breakdown (combined-only).
  const canSeeApprovals = useCanSeeLeadApprovals();
  const pendingApprovalsQuery = useLeadApprovalsPendingCount(canSeeApprovals, ownerId);
  const pendingApprovals = pendingApprovalsQuery.data?.count ?? 0;

  // Breakdown helper - reads the same tile key off the two slice results. When
  // a header range is active the This-month / Carry-forward split is meaningless
  // (the whole tile is one window), so `bd` returns undefined and the tile shows
  // just its headline.
  // `headline` is the tile's displayed value. Carry-forward is DERIVED as
  // headline - current so the two breakdown numbers ALWAYS sum to the tile,
  // even for the live follow-up/task counts (the combined / current / carry
  // slices are separate snapshots of constantly-changing data, so an
  // independently-fetched carry slice drifts a lead or two vs the headline).
  // headline - current is also the correct carry definition (created before
  // this month). Defaults to the combined (whole-pool) count for the tile.
  const bd = (
    key: keyof TrackerCounts & string,
    headline: number = combined[key] as number,
  ): TileBreakdown | undefined => {
    if (rangeActive) return undefined;
    const current = currentMonth[key] as number;
    return {
      currentMonth: current,
      carryForward: Math.max(0, headline - current),
      loading:
        combined.loading[key as keyof TrackerCounts['loading']] ||
        currentMonth.loading[key as keyof TrackerCounts['loading']],
    };
  };

  // Three-way breakdown (Current + Carry-forward + Unassigned) for the status
  // tiles that fold the no-agent pool into their headline (NQ / Active / Large
  // lead). Current + Carry are the assigned slices; `unassigned` is the fixed
  // global no-agent count. The three are disjoint and sum to the headline.
  const bd3 = (
    key: keyof TrackerCounts & string,
    unassigned: number,
  ): TileBreakdown | undefined => {
    if (rangeActive) return undefined;
    return {
      currentMonth: currentMonth[key] as number,
      carryForward: carryForward[key] as number,
      unassigned,
      loading:
        currentMonth.loading[key as keyof TrackerCounts['loading']] ||
        carryForward.loading[key as keyof TrackerCounts['loading']] ||
        combined.loading[key as keyof TrackerCounts['loading']],
    };
  };
  // Headline = Current + Carry + Unassigned for the folded tiles. In range mode
  // the assigned part comes from the single range-scoped slice; the global
  // unassigned pool is always added on top.
  const foldedValue = (key: keyof TrackerCounts & string, unassigned: number): number =>
    (rangeActive
      ? (combined[key] as number)
      : (currentMonth[key] as number) + (carryForward[key] as number)) + unassigned;

  // Aggregate tile values. Default view = This month + Carry forward (two
  // slices). Range active = the single range-scoped combined slice. Won / Lost
  // read the current-month slice by default (they count by closedAt), or the
  // range-scoped combined slice when a range is picked.
  // Total Leads = OPEN pool + this-period closures (Won + Lost). `totalLeads`
  // is now OPEN-only (see totalScopeQuery); Won/Lost count by closedAt in the
  // period, so the two sets are disjoint and simply add.
  // Assigned OPEN pool (Current + Carry) + this-period Won/Lost + the full
  // no-agent pool (combined.unassigned) folded in as the Unassigned row.
  const totalLeadsValue =
    (rangeActive
      ? combined.totalLeads + combined.won + combined.lost
      : currentMonth.totalLeads + carryForward.totalLeads + currentMonth.won + currentMonth.lost) +
    combined.unassigned;
  const totalLeadsLoading = rangeActive
    ? combined.loading.totalLeads ||
      combined.loading.won ||
      combined.loading.lost ||
      combined.loading.unassigned
    : currentMonth.loading.totalLeads ||
      carryForward.loading.totalLeads ||
      currentMonth.loading.won ||
      currentMonth.loading.lost ||
      combined.loading.unassigned;
  // Breakdown by CREATED date, matching the Overview report's "Current Month"
  // (leads CREATED this month, whether still open OR already closed this month)
  // vs "Carry forward" (everything else in the working pool). Earlier this filed
  // ALL this-period closures under Carry fwd, which mis-classified leads created
  // AND lost in the same month (e.g. 3 Aug-created Aug-lost leads showed the
  // inbox 190 while the report showed 193). `currentMonthCreatedCount` counts
  // created-this-month across every bucket; carry-forward is the remainder so
  // the two always sum to the headline.
  const currentMonthCreatedQuery = useLeadsList({
    page: 1,
    pageSize: 1,
    sortDir: 'desc',
    createdAtFrom: `${currentMonthKey}-01`,
    createdAtTo: todayIso,
    excludeWebsite: true,
    // Assigned-only: the no-agent pool is its own Unassigned breakdown row, so
    // Current + Carry + Unassigned stay disjoint and sum to the headline.
    assignedAgentIsNotNull: true,
    ...(ownerId ? { assignedAgentId: ownerId } : {}),
  });
  const currentMonthCreatedCount = currentMonthCreatedQuery.data?.pagination.totalItems ?? 0;

  // Follow-up / First Contact split for the Today's + Missed follow-up tiles.
  // "First Contact" = the Not Qualified (never-contacted) subset; "Follow-up" =
  // the rest (active, already-contacted). Combined (whole-pool) slice, no date
  // window, so it matches the tile headline; Follow-up = headline - firstContact.
  const fcBase = {
    page: 1 as const,
    pageSize: 1 as const,
    sortDir: 'desc' as const,
    bucket: 'OPEN' as const,
    excludeWebsite: true as const,
    includeUnassignedPool: true as const,
    currentStatusId: notQualifiedStatusId ?? ZERO_UUID,
    ...(ownerId ? { assignedAgentId: ownerId } : {}),
  };
  const todayFirstContactQuery = useLeadsList({
    ...fcBase,
    nextFollowUpFrom: todayIso,
    nextFollowUpTo: todayIso,
  });
  const missedFirstContactQuery = useLeadsList({ ...fcBase, nextFollowUpTo: yesterdayIso });
  // Follow-up vs First-contact counts drive the Today / Missed tile splits
  // directly (the redesign tiles render the two numbers inline rather than via
  // the legacy right-hand breakdown block).
  const todayFirstContact = todayFirstContactQuery.data?.pagination.totalItems ?? 0;
  const missedFirstContact = missedFirstContactQuery.data?.pagination.totalItems ?? 0;
  const totalLeadsBreakdown: TileBreakdown | undefined = rangeActive
    ? undefined
    : {
        // Assigned created-this-month; carry = the remaining assigned pool
        // (headline minus this-month minus the unassigned row); unassigned =
        // the full no-agent pool. The three sum to the headline.
        currentMonth: currentMonthCreatedCount,
        carryForward: Math.max(0, totalLeadsValue - currentMonthCreatedCount - combined.unassigned),
        unassigned: combined.unassigned,
        loading:
          currentMonthCreatedQuery.isLoading ||
          currentMonth.loading.totalLeads ||
          currentMonth.loading.won ||
          currentMonth.loading.lost ||
          carryForward.loading.totalLeads ||
          combined.loading.unassigned,
      };
  // NQ / Active / Large-lead fold the global no-agent pool into their headline.
  const notQualifiedValue = foldedValue('notQualified', combined.unassignedNotQualified);
  const activeLeadsValue = foldedValue('activeLeads', combined.unassignedActive);
  const largeLeadValue = foldedValue('largeLeadQuery', combined.unassignedLargeLead);
  const activeLeadsLoading = rangeActive
    ? combined.loading.activeLeads
    : currentMonth.loading.activeLeads ||
      carryForward.loading.activeLeads ||
      combined.loading.activeLeads;
  const longFollowupValue = rangeActive
    ? combined.longFollowup
    : currentMonth.longFollowup + carryForward.longFollowup;
  const longFollowupLoading = rangeActive
    ? combined.loading.longFollowup
    : currentMonth.loading.longFollowup || carryForward.loading.longFollowup;
  // Unassigned is a fixed GLOBAL count: every lead with no agent, ignoring the
  // date filter entirely (the 'all' slice carries no date window for it). Not a
  // dated pool, so no This-month / Carry-forward breakdown.
  const unassignedValue = combined.unassigned;
  const unassignedLoading = combined.loading.unassigned;
  // Won / Lost are assigned-only, scoped to THIS MONTH (by close date) so the
  // tile matches its drill-in. A closed lead has no current/carry split the way
  // an open lead does - "won this month" is a single cohort by close date - so
  // no breakdown rows (unassigned closed leads are out of scope per product).
  const wonValue = rangeActive ? combined.won : currentMonth.won;
  const wonLoading = rangeActive ? combined.loading.won : currentMonth.loading.won;
  const lostValue = rangeActive ? combined.lost : currentMonth.lost;
  const lostLoading = rangeActive ? combined.loading.lost : currentMonth.loading.lost;

  return (
    <div className="mb-3">
      <div
        data-tracker
        className={
          todayOnly
            ? 'grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-3 min-[1001px]:grid-cols-6'
            : 'grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-3 min-[1001px]:grid-cols-5'
        }
      >
        {/* ---- Row 1 (Today only): Total Tasks, Fresh, Today FU, Missed FU, Revert ---- */}
        {/* Total Tasks - DISTINCT union of the five task legs. Shown FIRST as
            the headline "what to action today" number. Clicking drills into the
            same `dailyTaskPool` union (server dedupes + computes "today"), so
            the aside list Total equals this tile. */}
        <TodayTile
          label="Total Tasks"
          value={combined.totalTasks}
          loading={combined.loading.totalTasks}
          dark
          numberColor="#ffffff"
          hint="Distinct leads needing action today"
          disabled={combined.totalTasks === 0}
          onClick={() =>
            void openTileViaLazyFetch(
              'dailyTaskPool:combined',
              {
                page: 1,
                pageSize: 1,
                sortDir: 'desc',
                dailyTaskPool: true,
                ...operationalCreatedRange,
              },
              'dailyTaskPool=true',
            )
          }
        />
        {/* Fresh Today - a today-only metric, no month/carry-forward split. */}
        <TodayTile
          label="Today's Fresh Leads"
          value={combined.freshToday}
          loading={combined.loading.freshToday}
          dotColor="#1d4ed8"
          numberColor="#1d4ed8"
          hint="Created today"
          disabled={combined.freshToday === 0}
          onClick={() => openTile('freshToday=true', firstLeadId(combined.firstId.freshToday))}
        />
        <TodayTile
          label="Today's Follow Up(s)"
          value={combined.todayFollowUps}
          loading={combined.loading.todayFollowUps}
          dotColor="#047857"
          numberColor="#047857"
          split={{
            leftLabel: 'Follow-up',
            left: Math.max(0, combined.todayFollowUps - todayFirstContact),
            rightLabel: 'First contact',
            right: todayFirstContact,
          }}
          disabled={combined.todayFollowUps === 0}
          onClick={() =>
            void openTileViaLazyFetch(
              'followupToday:combined',
              {
                page: 1,
                pageSize: 1,
                sortDir: 'desc',
                currentBucket: 'OPEN',
                nextFollowUpFrom: todayIso,
                nextFollowUpTo: todayIso,
                ...operationalCreatedRange,
              },
              `nextFollowUpFrom=${todayIso}&nextFollowUpTo=${todayIso}&bucket=OPEN`,
            )
          }
        />
        <TodayTile
          label="Missed Follow Up(s)"
          value={combined.missedFollowUps}
          loading={combined.loading.missedFollowUps}
          dotColor="#b91c1c"
          numberColor="#b91c1c"
          split={{
            leftLabel: 'Follow-up',
            left: Math.max(0, combined.missedFollowUps - missedFirstContact),
            rightLabel: 'First contact',
            right: missedFirstContact,
          }}
          disabled={combined.missedFollowUps === 0}
          onClick={() =>
            void openTileViaLazyFetch(
              'followupMissed:combined',
              {
                page: 1,
                pageSize: 1,
                sortDir: 'desc',
                currentBucket: 'OPEN',
                nextFollowUpTo: yesterdayIso,
                ...operationalCreatedRange,
              },
              `nextFollowUpTo=${yesterdayIso}&bucket=OPEN`,
            )
          }
        />
        <TodayTile
          label="Revert Pending"
          value={combined.revertPending}
          loading={combined.loading.revertPending}
          dotColor="#ea580c"
          numberColor="#094053"
          hint="Reply owed to the customer"
          disabled={combined.revertPending === 0}
          onClick={() =>
            void openTileViaLazyFetch(
              'revertPending:combined',
              {
                page: 1,
                pageSize: 1,
                sortDir: 'desc',
                revertPending: true,
                currentBucket: 'OPEN',
                ...operationalCreatedRange,
                ...(notQualifiedStatusId ? { excludeStatusId: notQualifiedStatusId } : {}),
              },
              'revertPending=true&bucket=OPEN',
            )
          }
        />
        {/* High Intent - OPEN leads auto-scored HIGH on Lead Intent (priority).
            In todayOnly mode the Row 3 queues strip is hidden, so surface High
            Intent here in the daily-tracker strip; in full mode it stays in the
            Row 3 queues (below) to avoid showing it twice. Same count + drill. */}
        {todayOnly && (
          <TodayTile
            label="High Intent"
            value={combined.highIntent}
            loading={combined.loading.highIntent}
            dotColor="#b45309"
            numberColor="#b45309"
            hint="Open leads with HIGH intent"
            disabled={combined.highIntent === 0}
            onClick={() =>
              void openTileViaLazyFetch(
                'highIntent:combined',
                {
                  page: 1,
                  pageSize: 1,
                  sortDir: 'desc',
                  priority: 'HIGH',
                  currentBucket: 'OPEN',
                  ...operationalCreatedRange,
                },
                'priority=HIGH&bucket=OPEN',
              )
            }
          />
        )}

        {/* ---- Row 2: pipeline / outcome (Total, NQ, Active, Won, Lost) ----
            Hidden in `todayOnly` mode (these pool metrics now live only in the
            month-filtered report below, from a single source). */}
        {!todayOnly && (
          <>
            {/* Total Leads = active pool = Current month + Carry-forward
            backlog (e.g. 514 + 157 = 671). An aggregate of the two
            breakdown slices, so it can't reuse the `f.combined` drill-in
            (that lists the whole all-time pool). Instead it opens the SAME
            working pool the pipeline list below shows - the active-month
            lens with carry-forward folded in (`?carry=1`). */}
            <TrackerTile
              label="Total Leads"
              value={totalLeadsValue}
              loading={totalLeadsLoading}
              tone="neutral"
              breakdown={totalLeadsBreakdown}
              disabled={totalLeadsValue === 0}
              onClick={() =>
                openWorkingPool(
                  {
                    page: 1,
                    pageSize: 1,
                    sortDir: 'desc',
                    createdAtFrom: `${currentMonthKey}-01`,
                    createdAtTo: todayIso,
                    includeActiveCarryover: true,
                    activeCarryoverFrom: CARRYFORWARD_FLOOR_ISO,
                    ...operationalCreatedRange,
                  },
                  '',
                )
              }
            />
            <TrackerTile
              label="Not Qualified"
              sublabel="(Not Contacted)"
              value={notQualifiedValue}
              loading={combined.loading.notQualified}
              tone="warning"
              disabled={!notQualifiedStatusId || notQualifiedValue === 0}
              breakdown={bd3('notQualified', combined.unassignedNotQualified)}
              onClick={() => {
                if (!notQualifiedStatusId) return;
                openTile(
                  `stage=${notQualifiedStatusId}`,
                  firstLeadId(combined.firstId.notQualified),
                );
              }}
            />
            {/* Active Leads - parent `active` stage, OPEN bucket. The headline
            is This month + Carry forward, so the drill-in takes the SAME
            bounded route as Total Leads (`f.activeCarryover=yes`), not
            `f.combined` - the combined flag drops every date bound and
            lists the all-time pool, which includes the pre-cutover legacy
            OPEN backlog the carry-forward floor deliberately excludes.
            That was the tile-says-183 / list-says-184 gap. */}
            <TrackerTile
              label="Active Leads"
              sublabel="Proposal + Hot + Warm"
              value={activeLeadsValue}
              loading={activeLeadsLoading}
              tone="open"
              breakdown={bd3('activeLeads', combined.unassignedActive)}
              disabled={!combined.activeStatusId || activeLeadsValue === 0}
              onClick={() => {
                if (!combined.activeStatusId) return;
                openWorkingPool(
                  {
                    page: 1,
                    pageSize: 1,
                    sortDir: 'desc',
                    currentStatusId: combined.activeStatusId,
                    currentBucket: 'OPEN',
                    createdAtFrom: `${currentMonthKey}-01`,
                    createdAtTo: todayIso,
                    includeActiveCarryover: true,
                    activeCarryoverFrom: CARRYFORWARD_FLOOR_ISO,
                    ...operationalCreatedRange,
                  },
                  `stage=${combined.activeStatusId}&bucket=OPEN`,
                );
              }}
            />
            {/* Won / Lost are period-outcome tiles: they count strictly by
            closedAt, so they show ONLY the current month (closed this
            month) - no Carry-forward row (carry-forward has no meaning for
            a closed lead). Value + drill-in both use the current-month
            slice so the tile and the drilled list Total agree. */}
            <TrackerTile
              label="Won"
              sublabel={rangeActive ? undefined : 'This month'}
              value={wonValue}
              loading={wonLoading}
              tone="won"
              disabled={wonValue === 0}
              onClick={() =>
                openTileMonth(
                  'bucket=WON',
                  firstLeadId(rangeActive ? combined.firstId.won : currentMonth.firstId.won),
                )
              }
            />
            <TrackerTile
              label="Lost"
              sublabel={rangeActive ? undefined : 'This month'}
              value={lostValue}
              loading={lostLoading}
              tone="lost"
              disabled={lostValue === 0}
              onClick={() =>
                openTileMonth(
                  'bucket=LOST',
                  firstLeadId(rangeActive ? combined.firstId.lost : currentMonth.firstId.lost),
                )
              }
            />
          </>
        )}
      </div>
      {!todayOnly && (
        <div className="mt-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {/* ---- Row 3: queues (High Intent, Large, Long FU, Unassigned, Pending Approvals) ---- */}
          {/* High Intent - OPEN leads auto-scored HIGH on Lead Intent. Drills to
            the first match's detail with ?priority=HIGH pre-applied. */}
          <TrackerTile
            label="High Intent"
            value={combined.highIntent}
            loading={combined.loading.highIntent}
            tone="warning"
            disabled={combined.highIntent === 0}
            onClick={() =>
              void openTileViaLazyFetch(
                'highIntent:combined',
                {
                  page: 1,
                  pageSize: 1,
                  sortDir: 'desc',
                  priority: 'HIGH',
                  currentBucket: 'OPEN',
                  ...operationalCreatedRange,
                },
                'priority=HIGH&bucket=OPEN',
              )
            }
          />
          <TrackerTile
            label="Large Lead Query"
            value={largeLeadValue}
            loading={combined.loading.largeLeadQuery}
            tone="warning"
            disabled={largeLeadValue === 0}
            breakdown={bd3('largeLeadQuery', combined.unassignedLargeLead)}
            onClick={() =>
              void openTileViaLazyFetch(
                'largeLeadQuery:combined',
                {
                  page: 1,
                  pageSize: 1,
                  sortDir: 'desc',
                  largeLeadQuery: true,
                  currentBucket: 'OPEN',
                  ...operationalCreatedRange,
                },
                'largeLeadQuery=true&bucket=OPEN',
              )
            }
          />
          {/* Long follow-up - parked leads (long_followup status, OPEN). Now part
            of the pool; This month + Carry-forward like the other tiles. */}
          <TrackerTile
            label="Long Follow-up"
            value={longFollowupValue}
            loading={longFollowupLoading}
            tone="neutral"
            breakdown={bd('longFollowup', longFollowupValue)}
            disabled={!combined.longFollowupStatusId || longFollowupValue === 0}
            onClick={() => {
              if (!combined.longFollowupStatusId) return;
              openWorkingPool(
                {
                  page: 1,
                  pageSize: 1,
                  sortDir: 'desc',
                  currentStatusId: combined.longFollowupStatusId,
                  createdAtFrom: `${currentMonthKey}-01`,
                  createdAtTo: todayIso,
                  includeActiveCarryover: true,
                  activeCarryoverFrom: CARRYFORWARD_FLOOR_ISO,
                  ...operationalCreatedRange,
                },
                `stage=${combined.longFollowupStatusId}`,
              );
            }}
          />
          {/* Unassigned - every lead with no agent (website excluded). Org-wide,
            so no owner scope; part of the pool. */}
          <TrackerTile
            label="Unassigned"
            sublabel="No agent"
            value={unassignedValue}
            loading={unassignedLoading}
            tone="warning"
            breakdown={bd('unassigned', unassignedValue)}
            disabled={unassignedValue === 0}
            onClick={() => navigate('/leads/exit-unassigned')}
          />
          {/* Pending Approvals - queue depth, no date breakdown. */}
          {canSeeApprovals && (
            <TrackerTile
              label="Pending Approvals"
              sublabel={ownerId ? `Raised by ${ownerName ?? 'selected owner'}` : 'Awaiting review'}
              value={pendingApprovals}
              loading={pendingApprovalsQuery.isLoading}
              tone="warning"
              disabled={pendingApprovals === 0}
              onClick={() =>
                navigate(
                  ownerId
                    ? `/leads/approvals?f.requestedBy=${ownerId}&f.status=PENDING`
                    : '/leads/approvals',
                )
              }
            />
          )}
        </div>
      )}
    </div>
  );
};
